import React, { useState, useEffect } from "react";
import api from "../../../const/axios";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CheckCircle2, ShoppingBag } from "lucide-react";

// ─── Field wrapper ────────────────────────────────────────────────────────────

const Field: React.FC<{ label: string; error?: string; children: React.ReactNode }> = ({ label, error, children }) => (
  <div className="space-y-1.5">
    <label className="text-sm font-semibold text-gray-700 dark:text-gray-200 block">{label}</label>
    {children}
    {error && <p className="text-red-500 text-xs">{error}</p>}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const Order: React.FC<any> = ({ user }) => {
  const { t } = useTranslation();

  const [form, setForm] = useState({
    name: "", surname: "1", email: "", phone: "", message: "", isHandled: false,
    topic: `Package order ${new Date()}`,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (user.isLoggedIn) {
      setForm(f => ({
        ...f,
        name:    (user.data.name || "") + " " + (user.data.surname || ""),
        surname: user.data.surname || "",
        email:   user.data.email   || "",
        phone:   user.data.phone   || "",
      }));
    }
  }, [user.isLoggedIn]);

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm(f => ({ ...f, [field]: e.target.value }));
    setErrors(er => ({ ...er, [field]: "" }));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.name.trim())                             errs.name    = t("contact.enterName");
    if (!form.email.trim() && form.phone.length < 10) errs.contact = t("contact.enterEmailOrPhone");
    if (form.message.length < 2)                      errs.message = t("order.enterGameDetails");
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      await api.post("contacts", { ...form });
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  const locked = user.isLoggedIn;

  // ── Success state ──────────────────────────────────────────────────────────

  if (sent) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16 flex items-center justify-center px-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-8 py-12 max-w-sm w-full text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-green-50 dark:bg-green-950 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-7 h-7 text-green-500" />
          </div>
          <h2 className="text-xl font-black text-gray-900 dark:text-gray-50">{t("order.thankYou")}</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Sifarişinizi aldıq. Ən qısa zamanda sizinlə əlaqə saxlayacağıq.</p>
          <Button variant="outline" className="w-full mt-2" onClick={() => setSent(false)}>
            Yeni sifariş
          </Button>
        </div>
      </div>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 pt-16">
      <Helmet>
        <title>{t("contact.writeToUs")} | 3sual.az</title>
      </Helmet>

      <div className="max-w-xl mx-auto px-4 sm:px-6 py-10">
        <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 shadow-sm px-6 sm:px-8 py-8 space-y-5">

          {/* Header */}
          <div className="flex items-center gap-3 pb-1">
            <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-950 flex items-center justify-center flex-shrink-0">
              <ShoppingBag className="w-4.5 h-4.5 text-blue-500 dark:text-indigo-300" />
            </div>
            <div>
              {/* <h1 className="text-xl font-black text-gray-900 dark:text-gray-50">{t("contact.writeToUs")}</h1> */}
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{t("order.description")}</p>
            </div>
          </div>

          {/* Name */}
          <Field label={t("contact.nameAndSurname")} error={errors.name}>
            <Input
              placeholder={t("contact.nameAndSurname")}
              value={form.name}
              onChange={set("name")}
              disabled={locked}
            />
          </Field>

          {/* Email + Phone */}
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("contact.email")} error={errors.contact}>
              <Input
                placeholder={t("contact.email")}
                value={form.email}
                onChange={set("email")}
                disabled={locked}
              />
            </Field>
            <Field label={t("contact.phone")}>
              <Input
                placeholder={t("contact.phone")}
                value={form.phone}
                onChange={set("phone")}
                disabled={locked}
              />
            </Field>
          </div>
          {errors.contact && <p className="text-red-500 text-xs -mt-3">{errors.contact}</p>}

          {/* Message */}
          <Field label={t("order.gamePlanDetails")} error={errors.message}>
            <textarea
              placeholder={t("order.gamePlanDetails")}
              value={form.message}
              onChange={set("message")}
              rows={6}
              className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-800 dark:text-gray-100 resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-transparent placeholder:text-gray-400 dark:placeholder:text-gray-500 disabled:opacity-50"
            />
          </Field>

          <Button onClick={handleSubmit} disabled={loading} className="w-full">
            {t("contact.send")}
          </Button>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ user }: any) => ({ user });
export default connect(mapStateToProps)(Order);
